import React from "react";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { useGetAdvanceSummariesQuery } from "../../../store/appApi";
import { formatCurrency, formatDateLabel, formatRoleLabel } from "./advancesConfig";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const balanceClass = (netBalance) => {
  if (netBalance > 0) return "text-rose-600";
  if (netBalance < 0) return "text-emerald-600";
  return "text-gray-500";
};

const AdvancesList = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useGetAdvanceSummariesQuery();

  const employees = Array.isArray(data?.employees) ? data.employees : [];
  const summary = data?.summary || {};
  const loading = isLoading || isFetching;

  return (
    <>
      <PageTitle activeMenu="Advances" motherMenu="Workforce" />

      <div className="space-y-6">
        <section className={`${panelClass} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Employee Advances
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
                Advances Ledger
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-500">
                Every employee with an advance or deduction on record. Click a
                row to open their full ledger.
              </p>
              <button
                type="button"
                className="mt-4 rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                onClick={() => navigate("/advances/add")}
              >
                + Add Advance
              </button>
            </div>

            <div className="grid min-w-[320px] grid-cols-3 gap-3 text-center text-xs text-gray-600">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Total Advance</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(summary.totalAdvance)}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Total Deduction</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(summary.totalDeduction)}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Net Balance</div>
                <div
                  className={`mt-1 text-lg font-semibold ${balanceClass(Number(summary.netBalance) || 0)}`}
                >
                  {formatCurrency(summary.netBalance)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Employee Balances
            </h2>
            <p className="text-sm text-gray-500">
              Net Balance = Total Advance − Total Deduction.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Employee</th>
                  <th className="px-5 py-2.5 text-left font-medium">Role</th>
                  <th className="px-5 py-2.5 text-right font-medium">
                    Advance Amount
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium">
                    Deduction
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium">
                    Net Balance
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium">
                    Last Entry
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-gray-500">
                      Loading advances...
                    </td>
                  </tr>
                )}

                {!loading && employees.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-gray-500">
                      No advances recorded yet.
                    </td>
                  </tr>
                )}

                {!loading &&
                  employees.map((employee) => (
                    <tr
                      key={employee.employeeId}
                      className="cursor-pointer transition hover:bg-gray-50"
                      onClick={() => navigate(`/advances/${employee.employeeId}`)}
                    >
                      <td className="px-5 py-2.5 font-semibold text-gray-900">
                        {employee.employeeName || "—"}
                        {!employee.employeeStatus && (
                          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {formatRoleLabel(employee.employeeRole)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-900">
                        {formatCurrency(employee.totalAdvance)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-900">
                        {formatCurrency(employee.totalDeduction)}
                      </td>
                      <td
                        className={`px-5 py-2.5 text-right font-semibold ${balanceClass(Number(employee.netBalance) || 0)}`}
                      >
                        {formatCurrency(employee.netBalance)}
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {formatDateLabel(employee.lastEntryDate)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default AdvancesList;
